/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Recovers a tab whose page code was replaced by a release (PH-2a).
 *
 * Every page is loaded on demand (React.lazy in main.tsx). A tab opened before
 * a release still names the code files of the old release; when it asks for
 * one the release removed, Vite raises `vite:preloadError` on window. Left
 * alone, the import fails and the customer sees an error, or - on the sign-in
 * page - nothing at all. Reloading once fetches the new release. This is
 * Vite's documented answer, and it covers what a cache header on the page
 * cannot: a tab that was already open during the release.
 *
 * What must still not happen, whatever else changes here:
 *   - a reload loop: a file that is genuinely missing would reload for ever,
 *     so a second error within RELOAD_WINDOW_MS of the last reload is left to
 *     show (the error boundary then names it);
 *   - a silent swallow: when no reload happens the event is NOT cancelled, so
 *     Vite re-throws the error and it reaches the error boundary and Sentry.
 * Blocked storage cannot stop the first reload; it can only make a second
 * one possible after the next release, which is still not a loop within a
 * page view, because the reload itself starts a new one.
 */

import { safeSessionStorage } from "@/helper/safeLocalStorage";

/** sessionStorage key holding the time of the last recovery reload. */
export const PRELOAD_RELOAD_KEY = "ds-preload-reload-at";

/** A second failure within this many ms of a recovery reload is not retried. */
export const RELOAD_WINDOW_MS = 10_000;

export const PRELOAD_ERROR_EVENT = "vite:preloadError";

/**
 * Handles one `vite:preloadError`. Exported for tests; production code uses
 * {@link installPreloadRecovery}.
 *
 * @returns true when it cancelled the error and reloaded the page.
 */
export function handlePreloadError(
  event: Event,
  reload: () => void = () => window.location.reload(),
  now: () => number = Date.now,
): boolean {
  const last = Number(safeSessionStorage.getItem(PRELOAD_RELOAD_KEY) ?? 0);
  const current = now();
  if (Number.isFinite(last) && current - last < RELOAD_WINDOW_MS) {
    return false; // reloaded moments ago: let the error show
  }
  safeSessionStorage.setItem(PRELOAD_RELOAD_KEY, String(current));
  event.preventDefault();
  reload();
  return true;
}

/**
 * Listens for `vite:preloadError` on `target` (window by default).
 *
 * @returns a function that removes the listener.
 */
export function installPreloadRecovery(
  target: Window = window,
  reload?: () => void,
): () => void {
  const listener = (event: Event) => {
    handlePreloadError(event, reload);
  };
  target.addEventListener(PRELOAD_ERROR_EVENT, listener);
  return () => target.removeEventListener(PRELOAD_ERROR_EVENT, listener);
}
